import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Card,
  Checkbox,
  Form,
  Header,
  Icon,
  Input,
  Label,
  Message,
  Modal,
} from 'semantic-ui-react';
import { useParams } from 'react-router-dom';
import {
  API,
  copy,
  getChannelModels,
  isRoot,
  showError,
  showFormalPricingWarningsV87,
  showInfo,
  showSuccess,
  showWarning,
} from '../../helpers';
import { CHANNEL_OPTIONS } from '../../constants';
import ChannelModelCopyV107 from './ChannelModelCopyV107';
import { KeyHelpV107 } from '../CommercialV106/KeyCommercialEditorV106';
import { renderChannelTip } from '../../helpers/render';
import useSmartBackV81 from '../../hooks/useSmartBackV81';
import { parseVertexCredentialV814 } from './vertexCredentialsV814';
import {
  buildChannelKeySubmissionV816,
  VERTEX_DEFAULT_IMAGE_CONCURRENCY_V815,
  VERTEX_DEFAULT_LLM_CONCURRENCY_V815,
  VERTEX_MAX_CONCURRENCY_V815,
} from './vertexConcurrencyV815';
import RootStepUpModalV815 from '../../components/RootStepUpModalV815';
import ChannelSecretReviewNoticeV815, {
  ChannelSecretReviewButtonV815,
} from './ChannelSecretReviewV815';
import {
  buildChannelEgressSubmissionV816,
  buildTailscaleServerOptionsV816,
  DEFAULT_TAILSCALE_PROXY_PORT_V816,
  isProxyEnabledV816,
  normalizeChannelEgressConfigV816,
} from './channelEgressV816';
import {
  buildGoogleGeminiServiceTierOptionsV816,
  buildOpenAICompatibleProfileOptionsV820,
  buildVertexOpenAICompatibleServiceTierOptionsV817,
  buildVertexProviderServiceTierOptionsV817,
  isOpenAICompatibleVertexGroupV817,
  usesCompatibleServiceTierV1015,
  normalizeGoogleGeminiConfigV816,
  normalizeVertexOpenAICompatibleConfigV817,
} from './googleGeminiConfigV816';
import OpenLuxProfileV1014 from './OpenLuxProfileEditorV1014';
import {
  selectCompatibleProfileV1014,
  buildOpenLuxSubmissionV1015,
} from './openluxConfigV1014';
import {
  buildChannelModelDiscoveryRequestV816,
  getDefaultChannelModelsV816,
  hasImportableDiscoveredModelsV816,
} from './channelModelDiscoveryV816';
import {
  canClearDelistedModelsV93,
  isInformationalModelDiscoverySourceV93,
} from './channelModelDiscoveryV93';
import { applyModelIdPrefixV820 as applyModelIdPrefix } from './channelModelVisibilityV820';
import ChannelModelMappingEditorV820 from './ChannelModelMappingEditorV820';
import OutboundClientProfileV95 from './OutboundClientProfileV95';
import OpenRouterRoutingV1015 from './OpenRouterRoutingV1015';
import { buildOpenRouterSubmissionV1015 } from './openrouterConfigV1015';
import VideoProtocolControlV1016 from './VideoProtocolControlV1016';
import { buildVideoProtocolSubmissionV1016 } from './videoProtocolV1016';
import FixedForwardedIPV1013 from './FixedForwardedIPV1013';
import { buildFixedForwardedIPV1013 } from './forwardedIPConfigV1013';
import ImageUpscalingV96 from './ImageUpscalingV96';
import ImageResponsePolicyV103 from './ImageResponsePolicyV103';
import { imageResponsePolicyV103 } from './imageResponseConfigV103';
import ModelSyncV103 from './ModelSyncV103';
import { validateSearchChannelV115 } from './searchV115';
import SearchEnginesV116 from './SearchEnginesV116';
import { validateScrapeChannelV116 } from './scrapeV116';
import { importChannelModelsV1018, upstreamModelV1018 } from './channelModelImportV1018';
import PublicModelIDsV113 from './PublicModelIDsV113';
import { publicModelConfigV113, publicModelIDsV113 } from './publicModelNamingV113';
import './channelModelImportV111.css';
import {
  buildModelSyncSubmissionV103,
  modelSyncSnapshotV103,
  modelSyncConfigV103,
  savedChannelIdV103,
} from './modelSyncConfigV103';
import {
  buildUpscalingConfigV96,
  preserveUpscalingOnRevealV96,
} from './upscalingConfigV96';
import { buildOutboundProfileSubmissionV95 } from './outboundProfileV95';
import ChannelKeyPolicyV95 from './ChannelKeyPolicyV95';
import CodingPlanPolicyV95, { CodingPlanKeyV95 } from './CodingPlanPolicyV95';
import {
  buildCodingPlanConfigV95,
  buildCodingPlanKeysV95,
  preserveCodingPlanRowsV95,
} from './codingPlanConfigV95';
import {
  buildChannelKeyPolicyV95,
  normalizeChannelKeyPolicyV95,
  preserveKeyPolicyOnRevealV95,
} from './keyPolicyConfigV95';
import {
  buildModelPreviewDraftV822,
  buildModelRulesSubmissionV822,
  modelRuleErrorTextV1015,
  parseModelRulesRowsV822,
} from './channelModelRulesV822';
import ChannelModelPreviewV822 from './ChannelModelPreviewV822';
import useTailscaleServersV91 from './useTailscaleServersV91';
import {
  buildMinerUConfigV913,
  getMinerUModeV913,
  getMinerUModelsV913,
  isMinerUChannelV913,
  MINERU_BASE_URL_V913,
  normalizeMinerUBaseURLV913,
  normalizeMinerUConfigV913,
  validateMinerUChannelV913,
} from './mineruConfigV913';

const requestTailscaleServersV91 = (options) =>
  API.get('/api/channel/tailscale/servers', {
    ...options,
    validateStatus: () => true,
  });

const requestModelPreviewV822 = (draft, options) =>
  API.post('/api/channel/model-preview', draft, options);

const BASE_URL_SUFFIX_OPTIONS = [
  { key: 'v1', text: '/v1 (OpenAI / Cockpit)', value: '/v1' },
  { key: 'v1beta', text: '/v1beta (Gemini)', value: '/v1beta' },
  { key: 'none', text: 'No API suffix', value: '' },
  { key: 'custom', text: 'Custom path', value: '__custom__' },
];

const MODEL_SORT_MODE_ADDED_V107 = 'added';
const MODEL_SORT_MODE_ALPHABETIC_V107 = 'alphabetic';
const modelSortValueV107 = (value) =>
  value && typeof value === 'object'
    ? value.text || value.value || value.key || ''
    : value;
const sortChannelModelsV107 = (models, mode) => {
  const values = Array.isArray(models) ? models : [];
  if (mode !== MODEL_SORT_MODE_ALPHABETIC_V107) {
    return values;
  }
  return [...values].sort((left, right) =>
    String(modelSortValueV107(left)).localeCompare(
      String(modelSortValueV107(right)),
      undefined,
      {
        numeric: true,
        sensitivity: 'base',
      }
    )
  );
};

const PROVIDER_INPUT_PROTOCOL_OPTIONS = [
  {
    key: 'legacy',
    text: '自动直通（默认；按客户端协议，不做跨协议转换）',
    value: '',
  },
  {
    key: 'responses',
    text: '手动：OpenAI Responses（上游 /v1/responses）',
    value: 'openai_responses',
  },
  {
    key: 'chat',
    text: '手动：OpenAI Chat Completions（上游 /v1/chat/completions）',
    value: 'openai_chat',
  },
];

const PROVIDER_FEATURE_OPTIONS = [
  { key: 'native_sse', label: '流式 SSE', chat: true },
  { key: 'function_tools', label: 'Function tools / tool calls', chat: true },
  { key: 'reasoning', label: 'Reasoning effort', chat: true },
  { key: 'stateful_conversation', label: 'previous_response_id / 状态会话' },
  { key: 'storage', label: 'Responses store' },
  { key: 'compaction', label: 'Responses compact' },
  { key: 'background', label: 'Background jobs' },
  { key: 'hosted_tools', label: 'Hosted tools' },
  { key: 'mcp', label: 'MCP tools' },
  { key: 'computer_use', label: 'Computer use' },
];
const splitBaseURL = (value = '') => {
  const normalizedValue = value.trim().replace(/\/+$/, '');
  for (const suffix of ['/v1beta', '/v1']) {
    if (normalizedValue.endsWith(suffix)) {
      return {
        host: normalizedValue.slice(0, -suffix.length),
        suffix,
        customSuffix: '',
      };
    }
  }

  const customPathMatch = normalizedValue.match(
    /^([a-z][a-z\d+.-]*:\/\/[^/?#]+)(\/[^?#]*)?([?#].*)?$/i
  );
  if (customPathMatch && (customPathMatch[2] || customPathMatch[3])) {
    return {
      host: customPathMatch[1],
      suffix: '__custom__',
      customSuffix: `${customPathMatch[2] || ''}${customPathMatch[3] || ''}`,
    };
  }

  return { host: normalizedValue, suffix: '', customSuffix: '' };
};

const joinBaseURL = (host = '', suffix = '', customSuffix = '') => {
  const normalizedHost = host.trim().replace(/\/+$/, '');
  if (!normalizedHost) {
    return '';
  }
  const selectedSuffix = suffix === '__custom__' ? customSuffix : suffix;
  const normalizedSuffix = selectedSuffix
    .trim()
    .replace(/^\/*/, '/')
    .replace(/\/+$/, '');
  return normalizedSuffix === '/'
    ? normalizedHost
    : `${normalizedHost}${normalizedSuffix}`;
};
const DEFAULT_CHANNEL_CONFIG_V72 = {
  fixed_forwarded_ip_enabled_v1013: false,
  fixed_forwarded_ip_v1013: '',
  region: '',
  sk: '',
  ak: '',
  user_id: '',
  egress_mode: 'direct',
  upstream_proxy: '',
  tailscale_server: '',
  tailscale_proxy_port: DEFAULT_TAILSCALE_PROXY_PORT_V816,
  vertex_ai_service_tier: '',
  openai_compatible_profile: '',
  outbound_client_profile_v95: '',
  gemini_enterprise_project_id: '',
  gemini_enterprise_discovery_adc: '',
  gemini_api_project_id: '',
  edge_proxy: false,
  disable_key_on_failure: true,
  key_failure_threshold_v95: 1,
  key_usage_strategy_v95: 'round_robin',
  coding_plan_cooldown_hours_v95: 5,
  test_endpoint: 'chat_completions',
  model_mapping_target_only_models: [],
};

const createEmptyKeyRow = () => ({
  quota_group_v95: '',
  id: `new-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  key_value: '',
  key_masked: false,
  key_display: '',
  status: 'enabled',
  last_error: '',
  last_used_at: '',
  cooldown_until: '',
  llm_concurrency_limit: VERTEX_DEFAULT_LLM_CONCURRENCY_V815,
  image_concurrency_limit: VERTEX_DEFAULT_IMAGE_CONCURRENCY_V815,
});

const buildKeyRowFromData = (key = {}) => ({
  quota_group_v95: key.quota_group_v95 || '',
  coding_plan_active_v95: Boolean(key.coding_plan_active_v95),
  coding_plan_retry_at_v95: key.coding_plan_retry_at_v95 || 0,
  coding_plan_reset_source_v95: key.coding_plan_reset_source_v95 || '',
  id: key.id ?? `key-${key.key_value}`,
  key_value: key.key_masked
    ? key.key_display || '********'
    : key.key_value ?? '',
  key_masked: Boolean(key.key_masked),
  key_display: key.key_display || '',
  status: key.status ?? 'enabled',
  last_error: key.last_error ?? '',
  last_used_at: key.last_used_at ?? '',
  cooldown_until: key.cooldown_until ?? '',
  llm_concurrency_limit:
    key.llm_concurrency_limit ?? VERTEX_DEFAULT_LLM_CONCURRENCY_V815,
  image_concurrency_limit:
    key.image_concurrency_limit ?? VERTEX_DEFAULT_IMAGE_CONCURRENCY_V815,
});

function type2secretPrompt(type, t) {
  switch (type) {
    case 15:
      return t('channel.edit.key_prompts.zhipu');
    case 18:
      return t('channel.edit.key_prompts.spark');
    case 22:
      return t('channel.edit.key_prompts.fastgpt');
    case 23:
      return t('channel.edit.key_prompts.tencent');
    case 53:
      return t('channel.edit.key_prompts.gemini_enterprise');
    case 54:
      return t('channel.edit.mineru_v913.token');
    case 56:
      return t('channel.edit.scrape_v116.token');
    case 55:
      return t('channel.edit.search_v115.token');
    default:
      return t('channel.edit.key_prompts.default');
  }
}

function buildKeyRows(data) {
  if (Array.isArray(data.keys) && data.keys.length > 0) {
    return data.keys.map((key) => buildKeyRowFromData(key));
  }
  if (data.key) {
    return data.key
      .split('\n')
      .map((keyValue) => keyValue.trim())
      .filter(Boolean)
      .map((keyValue) => ({
        ...createEmptyKeyRow(),
        key_value: keyValue,
      }));
  }
  return [createEmptyKeyRow()];
}

const buildVertexCredentialRowsV814 = (data, savedConfig = {}) => {
  const rows = buildKeyRows(data);
  const populatedRows = rows.filter((row) =>
    String(row.key_value || '').trim()
  );
  if (populatedRows.length === 0 && savedConfig.vertex_ai_adc) {
    const legacyProjectId = String(
      savedConfig.vertex_ai_project_id || ''
    ).trim();
    const legacyCredential = legacyProjectId
      ? `${savedConfig.region || ''}|${legacyProjectId}|${
          savedConfig.vertex_ai_adc
        }`
      : savedConfig.vertex_ai_adc;
    populatedRows.push({
      ...createEmptyKeyRow(),
      key_value: legacyCredential,
    });
  }
  return (populatedRows.length > 0 ? populatedRows : rows).map((row) => {
    try {
      const { keyValue } = parseVertexCredentialV814(row.key_value);
      return {
        ...row,
        key_value: JSON.stringify(JSON.parse(keyValue), null, 2),
      };
    } catch (_) {
      return row;
    }
  });
};

function formatDateTime(value) {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
}

const normalizeModelIdPrefix = (value = '') =>
  value.trim().replace(/^-+|-+$/g, '');

const stripModelIdPrefix = (modelName, prefix) => {
  const normalizedPrefix = normalizeModelIdPrefix(prefix);
  if (!normalizedPrefix) {
    return modelName;
  }
  const prefixWithSeparator = `${normalizedPrefix}-`;
  if (modelName.startsWith(prefixWithSeparator)) {
    return modelName.slice(prefixWithSeparator.length);
  }
  return modelName;
};

const mapModelsWithPrefix = (models, prefix, previousPrefix = '') => {
  const seen = new Set();
  return (models || [])
    .map((modelName) =>
      applyModelIdPrefix(stripModelIdPrefix(modelName, previousPrefix), prefix)
    )
    .filter((modelName) => {
      if (!modelName || seen.has(modelName)) {
        return false;
      }
      seen.add(modelName);
      return true;
    });
};

const EditChannel = () => {
  const { t } = useTranslation();
  const params = useParams();
  const returnToChannels = useSmartBackV81('/channel');
  const channelId = params.id;
  const isEdit = channelId !== undefined;
  const rootUser = useMemo(() => isRoot(), []);
  const [loading, setLoading] = useState(isEdit);
  const [savingV103, setSavingV103] = useState(false);
  const [modelSyncLoadingV103, setModelSyncLoadingV103] = useState(false);
  const [modelSyncStateV103, setModelSyncStateV103] = useState(null);
  const [savedModelSyncEnabledV103, setSavedModelSyncEnabledV103] =
    useState(false);
  const [modelSyncErrorV103, setModelSyncErrorV103] = useState('');
  const [modelSyncNeedsRefreshV103, setModelSyncNeedsRefreshV103] =
    useState(false);
  const modelSyncBusyV103 = useRef(false);
  const savingBusyV103 = useRef(false);
  const [recoveringKeyId, setRecoveringKeyId] = useState(null);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const [discoveryLoading, setDiscoveryLoading] = useState(false);
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [discoverySearch, setDiscoverySearch] = useState('');
  const [selectedDiscoveredModels, setSelectedDiscoveredModels] = useState([]);
  const handleCancel = () => {
    returnToChannels();
  };

  const originInputs = {
    name: '',
    type: 50,
    key: '',
    base_url: '',
    other: '',
    model_id_prefix: '',
    model_mapping: '',
    system_prompt: '',
    billing_provider_cost_profile: '',
    models: [],
    groups: ['default'],
  };
  const [inputs, setInputs] = useState(originInputs);
  const [baseURLHost, setBaseURLHost] = useState('');
  const [baseURLSuffix, setBaseURLSuffix] = useState('/v1');
  const [customBaseURLSuffix, setCustomBaseURLSuffix] = useState('');
  const [keyRows, setKeyRows] = useState([createEmptyKeyRow()]);
  const [originModelOptions, setOriginModelOptions] = useState([]);
  const [modelOptions, setModelOptions] = useState([]);
  const [modelSortModeV107, setModelSortModeV107] = useState(
    MODEL_SORT_MODE_ADDED_V107
  );
  const [groupOptions, setGroupOptions] = useState([]);
  const [costProfileOptions, setCostProfileOptions] = useState([]);
  const [costProfilesLoading, setCostProfilesLoading] = useState(false);
  const [customModel, setCustomModel] = useState('');
  const [config, setConfig] = useState({ ...DEFAULT_CHANNEL_CONFIG_V72 });
  const [openLuxDraftV1015, setOpenLuxDraftV1015] = useState({
    model: '',
    strategy: 'gemini_image',
    error: '',
  });
  const modelRulesRefV1015 = useRef(null);
  const [modelMappingRowsV820, setModelMappingRowsV820] = useState([]);
  const mineruChannelV913 = isMinerUChannelV913(inputs.type);
  const mineruModeV913 = getMinerUModeV913(config);
  const mineruAgentV913 = mineruChannelV913 && mineruModeV913 === 'agent';
  const mineruModelDraftsV913 = useRef({});
  const discoveryRevisionV913 = useRef(0);

  const resetDiscoveryV913 = () => {
    discoveryRevisionV913.current += 1;
    setDiscoveryResult(null);
    setDiscoveryLoading(false);
    setModelPickerOpen(false);
    setSelectedDiscoveredModels([]);
  };

  const handleMinerUModeChangeV913 = (e, { value }) => {
    if (value === mineruModeV913) return;
    mineruModelDraftsV913.current[mineruModeV913] = {
      models: inputs.models.map((model) =>
        stripModelIdPrefix(model, inputs.model_id_prefix)
      ),
      rows: modelMappingRowsV820,
    };
    const next = mineruModelDraftsV913.current[value];
    setInputs((current) => ({
      ...current,
      models: mapModelsWithPrefix(
        next?.models || getMinerUModelsV913({ mineru_mode: value }),
        current.model_id_prefix
      ),
    }));
    setModelMappingRowsV820(next?.rows || []);
    setConfig((current) => ({ ...current, mineru_mode: value }));
    resetDiscoveryV913();
  };
  const modelPreviewDraftV822 = useMemo(
    () =>
      buildModelPreviewDraftV822({
        type: inputs.type,
        models: inputs.models,
        modelIdPrefix: inputs.model_id_prefix,
        rows: modelMappingRowsV820,
        publicModelIDs: publicModelIDsV113(config),
        modelSyncProvider: config.model_sync_v103?.provider,
      }),
    [inputs.type, inputs.models, inputs.model_id_prefix, modelMappingRowsV820, config]
  );
  const {
    servers: tailscaleServersV816,
    loading: tailscaleServersLoadingV816,
    failed: tailscaleServersFailedV91,
    refresh: fetchTailscaleServersV816,
  } = useTailscaleServersV91(
    config.egress_mode === 'tailscale',
    requestTailscaleServersV91
  );
  const tailscaleServersErrorV816 = tailscaleServersFailedV91
    ? t('channel.edit.tailscale_servers_load_error')
    : '';
  const lastProxyModeV816 = useRef('proxy');
  const [rootStepUpOpenV815, setRootStepUpOpenV815] = useState(false);
  const [channelSecretExpiryV815, setChannelSecretExpiryV815] = useState(0);
  const geminiEnterpriseDiscoveryADCMaskedV816 =
    Array.isArray(config.masked_secret_fields_v815) &&
    config.masked_secret_fields_v815.includes(
      'gemini_enterprise_discovery_adc'
    );
  const channelSecretsMaskedV815 =
    isRoot() &&
    (keyRows.some((row) => row.key_masked) ||
      Array.isArray(config.masked_secret_fields_v815));
  const handleInputChange = (e, { name, value }) => {
    if (name === 'model_id_prefix') {
      setInputs((currentInputs) => {
        const nextPrefix = normalizeModelIdPrefix(value);
        return {
          ...currentInputs,
          model_id_prefix: nextPrefix,
          models: mapModelsWithPrefix(
            currentInputs.models,
            nextPrefix,
            currentInputs.model_id_prefix
          ),
        };
      });
      return;
    }
    if (name === 'type') {
      if (isMinerUChannelV913(value)) {
        setConfig((current) => normalizeMinerUConfigV913(value, current));
      }
      setInputs((currentInputs) => {
        const nextInputs = { ...currentInputs, [name]: value };
        if (isMinerUChannelV913(value) && currentInputs.type !== value) {
          nextInputs.base_url = currentInputs.base_url || MINERU_BASE_URL_V913;
          nextInputs.models = mapModelsWithPrefix(
            getMinerUModelsV913(config),
            currentInputs.model_id_prefix
          );
        } else if (Number(value) === 53 && !isEdit) {
          nextInputs.models = [];
        } else if (currentInputs.models.length === 0) {
          nextInputs.models = mapModelsWithPrefix(
            getDefaultChannelModelsV816(value, getChannelModels(value)),
            currentInputs.model_id_prefix
          );
        }
        return nextInputs;
      });
      resetDiscoveryV913();
      return;
    }
    setInputs((inputs) => ({ ...inputs, [name]: value }));
  };

  const updateOpenAICompatibleBaseURL = (host, suffix, customSuffix) => {
    setInputs((currentInputs) => ({
      ...currentInputs,
      base_url: joinBaseURL(host, suffix, customSuffix),
    }));
  };

  const handleOpenAIBaseURLHostChange = (e, { value }) => {
    setBaseURLHost(value);
    updateOpenAICompatibleBaseURL(value, baseURLSuffix, customBaseURLSuffix);
  };

  const handleOpenAIBaseURLSuffixChange = (e, { value }) => {
    setBaseURLSuffix(value);
    updateOpenAICompatibleBaseURL(baseURLHost, value, customBaseURLSuffix);
  };

  const handleCustomBaseURLSuffixChange = (e, { value }) => {
    setCustomBaseURLSuffix(value);
    updateOpenAICompatibleBaseURL(baseURLHost, baseURLSuffix, value);
  };

  const handleConfigChange = (e, { name, value, checked }) => {
    setConfig((inputs) => ({
      ...inputs,
      [name]: value !== undefined ? value : checked,
    }));
  };

  const handleOpenAICompatibleProfileChangeV820 = (e, { value }) => {
    setOpenLuxDraftV1015({ model: '', strategy: 'gemini_image', error: '' });
    setConfig((current) =>
      selectCompatibleProfileV1014(
        current,
        value,
        isOpenAICompatibleVertexGroupV817(inputs.type, inputs.groups)
      )
    );
  };

  const handleEgressModeChangeV816 = (egressMode) => {
    setConfig((currentConfig) => {
      if (currentConfig.egress_mode !== 'direct') {
        lastProxyModeV816.current = currentConfig.egress_mode;
      }
      if (egressMode !== 'direct') {
        lastProxyModeV816.current = egressMode;
      }
      return {
        ...currentConfig,
        egress_mode: egressMode,
        edge_proxy:
          egressMode === 'tailscale' ? false : currentConfig.edge_proxy,
      };
    });
  };

  const handleProxyEnabledChangeV816 = (e, { checked }) => {
    handleEgressModeChangeV816(
      checked ? lastProxyModeV816.current || 'proxy' : 'direct'
    );
  };

  const handleTailscaleServerChangeV816 = (e, { value }) => {
    const selectedServer = tailscaleServersV816.find(
      (server) =>
        String(server?.dns_name || '')
          .trim()
          .replace(/\.$/, '')
          .toLowerCase() === value
    );
    const parsedPort = Number.parseInt(selectedServer?.proxy_port, 10);
    setConfig((currentConfig) => ({
      ...currentConfig,
      tailscale_server: value,
      tailscale_proxy_port:
        Number.isInteger(parsedPort) && parsedPort > 0
          ? parsedPort
          : DEFAULT_TAILSCALE_PROXY_PORT_V816,
    }));
  };

  const testEndpointOptions = useMemo(
    () => [
      {
        key: 'chat_completions',
        value: 'chat_completions',
        text: t('channel.edit.test_endpoint.chat'),
      },
      {
        key: 'images_generations',
        value: 'images_generations',
        text: t('channel.edit.test_endpoint.image'),
      },
    ],
    [t]
  );

  const proxyEnabledV816 = isProxyEnabledV816(config);
  const tailscaleServerOptionsV816 = useMemo(
    () =>
      buildTailscaleServerOptionsV816(
        tailscaleServersV816,
        config.tailscale_server,
        {
          available: t('channel.edit.tailscale_status.available'),
          offline: t('channel.edit.tailscale_status.offline'),
          proxyUnavailable: t(
            'channel.edit.tailscale_status.proxy_unavailable'
          ),
          savedUnavailable: t(
            'channel.edit.tailscale_status.saved_unavailable'
          ),
        }
      ),
    [tailscaleServersV816, config.tailscale_server, t]
  );

  const providerProfile = config.protocol_capabilities_v5 || {
    protocol: '',
    supported_features: [],
    translatable_features: [],
  };

  const handleProviderProtocolChange = (e, { value }) => {
    setConfig((currentConfig) => {
      if (!value) {
        const nextConfig = { ...currentConfig };
        delete nextConfig.protocol_capabilities_v5;
        return nextConfig;
      }
      const supportedFeatures = ['native_sse', 'function_tools'];
      return {
        ...currentConfig,
        protocol_capabilities_v5: {
          protocol: value,
          supported_features: supportedFeatures,
          translatable_features:
            value === 'openai_chat' ? supportedFeatures : [],
        },
      };
    });
  };

  const handleProviderFeatureChange = (feature, checked) => {
    setConfig((currentConfig) => {
      const currentProfile = currentConfig.protocol_capabilities_v5;
      if (!currentProfile?.protocol) {
        return currentConfig;
      }
      const currentFeatures = currentProfile.supported_features || [];
      const supportedFeatures = checked
        ? Array.from(new Set([...currentFeatures, feature]))
        : currentFeatures.filter((item) => item !== feature);
      return {
        ...currentConfig,
        protocol_capabilities_v5: {
          ...currentProfile,
          supported_features: supportedFeatures,
          translatable_features:
            currentProfile.protocol === 'openai_chat'
              ? supportedFeatures.filter((item) =>
                  PROVIDER_FEATURE_OPTIONS.some(
                    (option) => option.key === item && option.chat
                  )
                )
              : [],
        },
      };
    });
  };
  const updateKeyRow = (index, patch) => {
    setKeyRows((rows) =>
      rows.map((row, rowIndex) =>
        rowIndex === index ? { ...row, ...patch } : row
      )
    );
  };

  const replaceKeyRowById = (keyId, nextRow) => {
    setKeyRows((rows) =>
      rows.map((row) => (String(row.id) === String(keyId) ? nextRow : row))
    );
  };

  const addKeyRow = () => {
    setKeyRows((rows) => [...rows, createEmptyKeyRow()]);
  };

  const removeKeyRow = (index) => {
    setKeyRows((rows) => {
      const nextRows = rows.filter((_, rowIndex) => rowIndex !== index);
      return nextRows.length > 0 ? nextRows : [createEmptyKeyRow()];
    });
  };

  const moveKeyRow = (index, direction) => {
    setKeyRows((rows) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= rows.length) {
        return rows;
      }
      const nextRows = [...rows];
      [nextRows[index], nextRows[nextIndex]] = [
        nextRows[nextIndex],
        nextRows[index],
      ];
      return nextRows;
    });
  };

  const getSubmissionKeyValues = () => {
    if (keyRows.some((row) => row.key_masked)) {
      return [];
    }
    if (
      inputs.type === 33 &&
      config.ak.trim() &&
      config.sk.trim() &&
      config.region.trim()
    ) {
      return [`${config.ak}|${config.sk}|${config.region}`];
    }
    if (inputs.type === 42) {
      const values = [];
      const seenProjects = new Set();
      keyRows.forEach((row) => {
        const rawValue = String(row.key_value || '').trim();
        if (!rawValue) return;
        const { projectId, keyValue } = parseVertexCredentialV814(rawValue);
        if (seenProjects.has(projectId)) {
          throw new Error(
            `Vertex AI project ${projectId} can only appear once in a channel.`
          );
        }
        seenProjects.add(projectId);
        values.push(keyValue);
      });
      return values;
    }
    const values = [];
    const seen = new Set();
    keyRows.forEach((row) => {
      const value = row.key_value.trim();
      if (!value || seen.has(value)) {
        return;
      }
      seen.add(value);
      values.push(value);
    });
    return values;
  };

  const isVideoTaskChannelV1 = () =>
    inputs.type === 52 && config.video_protocol_v1016 !== 'openrouter';
  const requiresDiscoveryBaseURL = () =>
    inputs.type === 8 || inputs.type === 50;

  const canDiscoverModels = () => {
    if (isVideoTaskChannelV1()) {
      return false;
    }
    if (!requiresDiscoveryBaseURL()) {
      return true;
    }
    return Boolean(inputs.base_url && inputs.base_url.trim());
  };

  const getDiscoverySourceText = (source) => {
    switch (source) {
      case 'dynamic':
        return t('channel.edit.discovery.sources.dynamic');
      case 'fallback_static':
        return t('channel.edit.discovery.sources.fallback_static');
      case 'reference_catalog':
        return t('channel.edit.discovery.sources.reference_catalog');
      case 'supported_static':
        return t('channel.edit.discovery.sources.supported_static');
      default:
        return t('channel.edit.discovery.sources.manual_only');
    }
  };

  const getDiscoveryMessageText = (data) => {
    if (data?.source === 'supported_static' && mineruChannelV913) {
      return t('channel.edit.mineru_v913.capabilities');
    }
    if (data?.source === 'reference_catalog') {
      return t('channel.edit.discovery.ark_reference_message_v93');
    }
    if (data?.source === 'manual_only') {
      return t('channel.edit.discovery.manual_only_message');
    }
    return data?.message || '';
  };

  const getDiscoveryWarningMessage = (data) => {
    if (!data) {
      return '';
    }
    const parts = [data.message].filter(Boolean);
    if (data.debug_endpoint) {
      parts.push(
        `${t('channel.edit.discovery.endpoint')}: ${data.debug_endpoint}`
      );
    }
    if (data.debug_error) {
      parts.push(
        `${t('channel.edit.discovery.debug_error')}: ${data.debug_error}`
      );
    }
    return parts.join(' | ');
  };

  const getKeyStatusColor = (status) => {
    switch (status) {
      case 'cooldown':
        return 'orange';
      case 'disabled':
        return 'red';
      default:
        return 'green';
    }
  };

  const getKeyStatusText = (status) => {
    switch (status) {
      case 'cooldown':
        return t('channel.edit.key_status.cooldown');
      case 'disabled':
        return t('channel.edit.key_status.disabled');
      default:
        return t('channel.edit.key_status.enabled');
    }
  };

  const loadChannel = async () => {
    try {
      let res = await API.get(`/api/channel/${channelId}`);
      const { success, message, data } = res.data;
      if (success) {
        if (!savedChannelIdV103(channelId, data.id)) {
          throw new Error('model_sync_v103.identity_error');
        }
        if (data.models === '') {
          data.models = [];
        } else {
          data.models = data.models.split(',');
        }
        if (data.group === '') {
          data.groups = [];
        } else {
          data.groups = data.group.split(',');
        }
        data.model_id_prefix = normalizeModelIdPrefix(
          data.model_id_prefix || ''
        );
        if (Number(data.type) === 50) {
          const parsedBaseURL = splitBaseURL(data.base_url || '');
          setBaseURLHost(parsedBaseURL.host);
          setBaseURLSuffix(parsedBaseURL.suffix);
          setCustomBaseURLSuffix(parsedBaseURL.customSuffix);
        }
        const savedConfig = data.config ? JSON.parse(data.config) : {};
        const savedSyncPolicy = modelSyncConfigV103(savedConfig);
        setSavedModelSyncEnabledV103(
          savedSyncPolicy.remove_unavailable ||
            savedSyncPolicy.add_free ||
            savedSyncPolicy.add_all_new ||
            savedSyncPolicy.remove_paid
        );
        setModelSyncStateV103(savedConfig.model_sync_state_v103 || null);
        delete savedConfig.model_sync_state_v103;
        delete data.model_sync_state_v103;
        setModelSyncNeedsRefreshV103(false);
        setModelSyncErrorV103('');
        if (isMinerUChannelV913(data.type)) {
          data.base_url = data.base_url || MINERU_BASE_URL_V913;
          mineruModelDraftsV913.current = {};
        }
        setModelMappingRowsV820(
          parseModelRulesRowsV822(
            data.model_mapping,
            savedConfig,
            data.models,
            data.model_id_prefix
          )
        );
        setInputs(data);
        if (Number(data.type) === 42) {
          setKeyRows(buildVertexCredentialRowsV814(data, savedConfig));
          delete savedConfig.vertex_ai_project_id;
          delete savedConfig.vertex_ai_adc;
        } else {
          setKeyRows(buildKeyRows(data));
        }
        const normalizedSavedConfigV816 = normalizeGoogleGeminiConfigV816(
          data.type,
          normalizeChannelEgressConfigV816(savedConfig)
        );
        setConfig({
          ...DEFAULT_CHANNEL_CONFIG_V72,
          ...normalizeMinerUConfigV913(
            data.type,
            normalizeChannelKeyPolicyV95(normalizedSavedConfigV816)
          ),
        });
        if (normalizedSavedConfigV816.egress_mode === 'tailscale') {
          lastProxyModeV816.current = 'tailscale';
        }
      } else {
        showError(message);
      }
    } catch (error) {
      showError(
        t(
          error.message === 'model_sync_v103.identity_error'
            ? error.message
            : 'model_sync_v103.load_error'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const syncModelsV103 = async () => {
    const savedId = savedChannelIdV103(channelId, inputs.id);
    if (
      !savedId ||
      loading ||
      modelSyncBusyV103.current ||
      savingBusyV103.current ||
      (!modelSyncNeedsRefreshV103 &&
        (!savedModelSyncEnabledV103 || inputs.status !== 1)) ||
      (modelSyncStateV103?.status === 'running' && !modelSyncNeedsRefreshV103)
    )
      return;
    modelSyncBusyV103.current = true;
    setModelSyncLoadingV103(true);
    setModelSyncErrorV103('');
    let refreshing = modelSyncNeedsRefreshV103;
    try {
      if (!refreshing) {
        const res = await API.post(
          `/api/channel/${savedId}/models/sync`,
          undefined,
          { validateStatus: () => true }
        );
        const { success, message, data } = res.data;
        if (data && typeof data === 'object') setModelSyncStateV103(data);
        if (!success) {
          setModelSyncErrorV103(message || t('model_sync_v103.sync_error'));
          return;
        }
        // The sync endpoint changes saved models; do not save the old editor snapshot.
        refreshing = true;
        setModelSyncNeedsRefreshV103(true);
      }
      const res = await API.get(`/api/channel/${savedId}`);
      if (!res.data?.success) throw new Error('model_sync_v103.refresh_error');
      const snapshot = modelSyncSnapshotV103(res.data.data, savedId);
      setInputs((current) => ({
        ...current,
        models: snapshot.models,
        model_mapping: res.data.data.model_mapping,
        config: res.data.data.config,
      }));
      const refreshedConfig = JSON.parse(res.data.data.config || '{}');
      setModelMappingRowsV820(
        parseModelRulesRowsV822(
          res.data.data.model_mapping,
          refreshedConfig,
          snapshot.models,
          res.data.data.model_id_prefix
        )
      );
      setModelSyncStateV103(snapshot.state);
      setModelSyncNeedsRefreshV103(false);
      resetDiscoveryV913();
    } catch (error) {
      if (!refreshing && error.response?.data?.data) {
        setModelSyncStateV103(error.response.data.data);
      }
      if (!refreshing && !error.response?.data?.data) {
        // A transport failure may hide a completed sync; refresh before saving.
        setModelSyncNeedsRefreshV103(true);
      }
      setModelSyncErrorV103(
        refreshing
          ? t('model_sync_v103.refresh_error')
          : error.response?.data?.message || t('model_sync_v103.sync_error')
      );
    } finally {
      modelSyncBusyV103.current = false;
      setModelSyncLoadingV103(false);
    }
  };

  const revealChannelSecretsV815 = async () => {
    const res = await API.post('/api/security/root/secrets/reveal', {
      kind: 'channel',
      id: Number(channelId),
    });
    if (!res.data?.success) {
      if (res.data?.code === 'step_up_required') {
        setRootStepUpOpenV815(true);
      } else {
        showError(res.data?.message);
      }
      return;
    }
    const revealed = res.data.data.channel;
    const savedConfig = revealed.config ? JSON.parse(revealed.config) : {};
    setKeyRows((current) =>
      preserveCodingPlanRowsV95(
        current,
        Number(revealed.type) === 42
          ? buildVertexCredentialRowsV814(revealed, savedConfig)
          : buildKeyRows(revealed)
      )
    );
    if (Number(revealed.type) === 42) {
      delete savedConfig.vertex_ai_project_id;
      delete savedConfig.vertex_ai_adc;
    }
    const normalizedSavedConfigV816 = normalizeGoogleGeminiConfigV816(
      revealed.type,
      normalizeChannelEgressConfigV816(savedConfig)
    );
    setConfig((current) =>
      preserveUpscalingOnRevealV96(
        current,
        preserveKeyPolicyOnRevealV95(current, {
          ...DEFAULT_CHANNEL_CONFIG_V72,
          ...normalizedSavedConfigV816,
          model_sync_v103: current.model_sync_v103,
          ...(isMinerUChannelV913(inputs.type)
            ? { mineru_mode: getMinerUModeV913(current) }
            : {}),
        })
      )
    );
    if (normalizedSavedConfigV816.egress_mode === 'tailscale') {
      lastProxyModeV816.current = 'tailscale';
    }
    const expiresAt = Date.parse(res.data.data.expires_at);
    setChannelSecretExpiryV815(
      Number.isNaN(expiresAt) ? Date.now() + 10 * 60 * 1000 : expiresAt
    );
  };

  const handleEnableKeyV2 = async (row) => {
    if (!isEdit) {
      return;
    }
    const parsedKeyId = Number.parseInt(String(row.id), 10);
    if (!Number.isInteger(parsedKeyId)) {
      showError(t('channel.edit.key_actions.reenable_invalid'));
      return;
    }

    setRecoveringKeyId(parsedKeyId);
    try {
      const res = await API.post(
        `/api/channel/${channelId}/key/${parsedKeyId}/enable_v2`
      );
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return;
      }
      replaceKeyRowById(parsedKeyId, buildKeyRowFromData(data));
      showSuccess(t('channel.edit.key_actions.reenable_success'));
    } catch (error) {
      showError(error.message);
    } finally {
      setRecoveringKeyId(null);
    }
  };

  const fetchBillingCostProfiles = async () => {
    if (!rootUser) return;
    setCostProfilesLoading(true);
    try {
      const response = await API.get(
        '/api/billing/v8/admin/provider-cost-profiles'
      );
      if (!response?.data?.success) {
        throw new Error(response?.data?.message);
      }
      setCostProfileOptions(
        (response.data.data || []).map((profile) => ({
          key: profile.id,
          value: profile.profile_key,
          text: `${profile.profile_key} · ${profile.provider} / ${
            profile.service_tier || '-'
          } / ${profile.region || '-'}`,
        }))
      );
    } catch (error) {
      showError(
        error.message || t('channel.edit.billing_cost_profile_load_error')
      );
    } finally {
      setCostProfilesLoading(false);
    }
  };

  const fetchModels = async () => {
    try {
      let res = await API.get(`/api/channel/models`);
      let localModelOptions = res.data.data.map((model) => ({
        key: model.id,
        text: model.id,
        value: model.id,
      }));
      setOriginModelOptions(localModelOptions);
    } catch (error) {
      showError(error.message);
    }
  };

  const fetchGroups = async () => {
    try {
      let res = await API.get(`/api/group/`);
      setGroupOptions(
        res.data.data.map((group) => ({
          key: group,
          text: group,
          value: group,
        }))
      );
    } catch (error) {
      showError(error.message);
    }
  };

  useEffect(() => {
    const localModelOptionMap = new Map();
    let availableOptions = originModelOptions;
    if (mineruChannelV913) {
      try {
        availableOptions = getMinerUModelsV913({
          mineru_mode: mineruModeV913,
        }).map((value) => ({ value }));
      } catch (_) {
        availableOptions = [];
      }
    }
    availableOptions.forEach((option) => {
      const modelName = applyModelIdPrefix(
        option.value,
        inputs.model_id_prefix
      );
      localModelOptionMap.set(modelName, {
        key: modelName,
        text: modelName,
        value: modelName,
      });
    });
    inputs.models.forEach((model) => {
      localModelOptionMap.set(model, {
        key: model,
        text: model,
        value: model,
      });
    });
    setModelOptions(Array.from(localModelOptionMap.values()));
  }, [
    originModelOptions,
    inputs.model_id_prefix,
    inputs.models,
    mineruChannelV913,
    mineruModeV913,
  ]);

  const displayedModelValuesV107 = useMemo(
    () => sortChannelModelsV107(inputs.models, modelSortModeV107),
    [inputs.models, modelSortModeV107]
  );
  const displayedModelOptionsV107 = useMemo(
    () => sortChannelModelsV107(modelOptions, modelSortModeV107),
    [modelOptions, modelSortModeV107]
  );
  const handleModelSelectionChangeV107 = (event, { value }) => {
    handleInputChange(event, {
      name: 'models',
      value: sortChannelModelsV107(value, modelSortModeV107),
    });
  };

  useEffect(() => {
    if (isEdit) {
      loadChannel().then();
    } else {
      setInputs((currentInputs) => ({
        ...currentInputs,
        models: mapModelsWithPrefix(
          getChannelModels(currentInputs.type),
          currentInputs.model_id_prefix
        ),
      }));
    }
    fetchModels().then();
    fetchGroups().then();
    fetchBillingCostProfiles().then();
  }, []);

  useEffect(() => {
    if (!channelSecretExpiryV815) return undefined;
    const timeout = window.setTimeout(() => {
      setKeyRows((rows) =>
        rows.map((row) =>
          Number.isInteger(Number(row.id))
            ? {
                ...row,
                key_value: row.key_display || '********',
                key_display: row.key_display || '********',
                key_masked: true,
              }
            : row
        )
      );
      setConfig((current) => {
        const next = { ...current };
        const maskedFields = [
          'ak',
          'sk',
          'vertex_ai_adc',
          'gemini_enterprise_discovery_adc',
          'upscale_api_key_v96',
        ].filter((field) => String(next[field] || '').trim() !== '');
        maskedFields.forEach((field) => delete next[field]);
        if (maskedFields.length > 0) {
          next.masked_secret_fields_v815 = maskedFields;
        }
        return next;
      });
      setChannelSecretExpiryV815(0);
    }, Math.max(0, channelSecretExpiryV815 - Date.now()));
    return () => window.clearTimeout(timeout);
  }, [channelSecretExpiryV815]);

  const submit = async () => {
    if (
      modelSyncBusyV103.current ||
      savingBusyV103.current ||
      modelSyncNeedsRefreshV103 ||
      loading ||
      (isEdit && !savedChannelIdV103(channelId, inputs.id))
    )
      return;
    let keyPolicyConfigV95;
    try {
      validateSearchChannelV115(inputs.type, inputs.base_url, config);
      validateScrapeChannelV116(inputs.type, inputs.base_url, config);
      const profileConfig = buildVideoProtocolSubmissionV1016(
        buildOpenRouterSubmissionV1015(
          buildOpenLuxSubmissionV1015(config, openLuxDraftV1015),
          inputs.type
        ),
        inputs.type
      );
      const imagePolicy = imageResponsePolicyV103(profileConfig);
      const imageConfig = buildFixedForwardedIPV1013(
        profileConfig,
        inputs.type
      );
      if (config.image_response_processing_v103) {
        imageConfig.image_response_processing_v103 = imagePolicy.processing;
      }
      validateMinerUChannelV913({
        type: inputs.type,
        config,
        baseURL: inputs.base_url,
        keyRows,
        isEdit,
      });
      keyPolicyConfigV95 = buildMinerUConfigV913(
        inputs.type,
        buildUpscalingConfigV96(
          buildCodingPlanConfigV95(
            buildChannelKeyPolicyV95(buildModelSyncSubmissionV103(imageConfig))
          )
        )
      );
    } catch (error) {
      if (error.message === 'channel.edit.openlux_v1014.invalid') {
        setOpenLuxDraftV1015((draft) => ({ ...draft, error: error.message }));
        document.querySelector('.openlux-add-v1014 input')?.focus();
      }
      showError(t(error.message));
      return;
    }
    let keyValues;
    let keyConfigsV815 = [];
    try {
      if (mineruAgentV913) {
        keyValues = [];
      } else if ([42, 53].includes(inputs.type)) {
        const submission = buildChannelKeySubmissionV816(inputs.type, keyRows);
        keyConfigsV815 = submission.keyConfigs;
        keyValues = submission.keyValues;
      } else {
        keyValues = getSubmissionKeyValues();
        if (
          inputs.type !== 33 &&
          (config.key_usage_strategy_v95 === 'coding_plan' ||
            keyRows.some((row) => row.quota_group_v95))
        ) {
          keyConfigsV815 = buildCodingPlanKeysV95(keyRows);
          keyValues = keyConfigsV815
            .map((row) => row.key_value)
            .filter(Boolean);
        }
      }
    } catch (error) {
      showError(t(error.message));
      return;
    }
    const hasSubmissionCredential =
      mineruAgentV913 ||
      keyValues.length > 0 ||
      Boolean(inputs.base_url && inputs.base_url.trim());
    if (!isEdit && (inputs.name === '' || !hasSubmissionCredential)) {
      const accessRequiredMessage = t('channel.edit.messages.access_required');
      showInfo(
        accessRequiredMessage === 'channel.edit.messages.access_required'
          ? '请填写渠道名称，并至少提供一种接入信息（API 密钥或 Base URL）！'
          : accessRequiredMessage
      );
      return;
    }
    if (inputs.type !== 43 && inputs.models.length === 0) {
      showInfo(t('channel.edit.messages.models_required'));
      return;
    }
    const modelMappingSubmissionV820 = buildModelRulesSubmissionV822(
      modelMappingRowsV820,
      inputs.models,
      inputs.model_id_prefix
    );
    if (modelMappingSubmissionV820.error) {
      showInfo(modelRuleErrorTextV1015(modelMappingSubmissionV820, t));
      modelRulesRefV1015.current?.querySelector('.error input')?.focus();
      return;
    }
    let localInputs = {
      ...inputs,
      key: keyValues[0] || '',
      keys: keyValues,
      key_configs: keyConfigsV815,
      model_id_prefix: normalizeModelIdPrefix(inputs.model_id_prefix),
      model_mapping: modelMappingSubmissionV820.modelMapping,
    };
    delete localInputs.model_sync_state_v103;
    if (mineruChannelV913) {
      localInputs.base_url = normalizeMinerUBaseURLV913(inputs.base_url);
      localInputs.system_prompt = '';
    }
    if (localInputs.base_url && localInputs.base_url.endsWith('/')) {
      localInputs.base_url = localInputs.base_url.slice(
        0,
        localInputs.base_url.length - 1
      );
    }
    if (localInputs.type === 3 && localInputs.other === '') {
      localInputs.other = '2024-03-01-preview';
    }
    let res;
    localInputs.models = localInputs.models.join(',');
    localInputs.group = localInputs.groups.join(',');
    const submissionConfig = buildChannelEgressSubmissionV816(
      normalizeVertexOpenAICompatibleConfigV817(
        localInputs.type,
        localInputs.group,
        normalizeGoogleGeminiConfigV816(localInputs.type, keyPolicyConfigV95)
      )
    );
    if (localInputs.type === 42) {
      delete submissionConfig.vertex_ai_project_id;
      delete submissionConfig.vertex_ai_adc;
    }
    if (localInputs.type !== 53) {
      delete submissionConfig.gemini_enterprise_discovery_adc;
      if (Array.isArray(submissionConfig.masked_secret_fields_v815)) {
        submissionConfig.masked_secret_fields_v815 =
          submissionConfig.masked_secret_fields_v815.filter(
            (field) => field !== 'gemini_enterprise_discovery_adc'
          );
        if (submissionConfig.masked_secret_fields_v815.length === 0) {
          delete submissionConfig.masked_secret_fields_v815;
        }
      }
    }
    if (modelMappingSubmissionV820.targetOnlyModels.length > 0) {
      submissionConfig.model_mapping_target_only_models =
        modelMappingSubmissionV820.targetOnlyModels;
    } else {
      delete submissionConfig.model_mapping_target_only_models;
    }
    if (Object.keys(modelMappingSubmissionV820.policies).length > 0) {
      submissionConfig.model_request_policies_v822 =
        modelMappingSubmissionV820.policies;
    } else {
      delete submissionConfig.model_request_policies_v822;
    }
    if (modelMappingSubmissionV820.disabledRules.length > 0) {
      submissionConfig.model_rules_disabled_v107 =
        modelMappingSubmissionV820.disabledRules;
    } else {
      delete submissionConfig.model_rules_disabled_v107;
    }
    localInputs.config = JSON.stringify(
      buildModelSyncSubmissionV103(
        buildOutboundProfileSubmissionV95(
          localInputs.type,
          localInputs.group,
          publicModelConfigV113(submissionConfig)
        ),
        modelSyncStateV103
      )
    );
    savingBusyV103.current = true;
    setSavingV103(true);
    try {
      if (isEdit) {
        res = await API.put(`/api/channel/`, {
          ...localInputs,
          id: parseInt(channelId),
        });
      } else {
        res = await API.post(`/api/channel/`, localInputs);
      }
      const { success, message, pricing_warnings } = res.data;
      if (success) {
        setOpenLuxDraftV1015({
          model: '',
          strategy: 'gemini_image',
          error: '',
        });
        showFormalPricingWarningsV87(pricing_warnings, t);
        if (isEdit) {
          showSuccess(t('channel.edit.messages.update_success'));
          await loadChannel();
        } else {
          showSuccess(t('channel.edit.messages.create_success'));
          setInputs({
            ...originInputs,
            models: mapModelsWithPrefix(
              getChannelModels(originInputs.type),
              originInputs.model_id_prefix
            ),
          });
          setKeyRows([createEmptyKeyRow()]);
          setModelMappingRowsV820([]);
          setConfig((currentConfig) => ({
            ...currentConfig,
            model_mapping_target_only_models: [],
            model_request_policies_v822: {},
            model_rules_disabled_v107: [],
          }));
          setDiscoveryResult(null);
        }
      } else {
        showError(message);
      }
    } catch (error) {
      showError(
        error.response?.data?.message || t('model_sync_v103.save_error')
      );
    } finally {
      savingBusyV103.current = false;
      setSavingV103(false);
    }
  };

  const fetchDiscoveredModels = async () => {
    if (isVideoTaskChannelV1()) {
      showInfo(
        'Model discovery is unavailable for this video protocol.'
      );
      return null;
    }
    if (!canDiscoverModels()) {
      showInfo(t('channel.edit.messages.base_url_required'));
      return null;
    }
    let keyValues = [];
    try {
      if (!isEdit && !mineruChannelV913) {
        keyValues = getSubmissionKeyValues();
      }
    } catch (error) {
      showError(error.message);
      return null;
    }
    const discoveryRevision = ++discoveryRevisionV913.current;
    setDiscoveryLoading(true);
    try {
      const res = await API.post(
        '/api/channel/models/discover',
        buildChannelModelDiscoveryRequestV816({
          isEdit,
          channelId,
          type: inputs.type,
          baseURL: inputs.base_url,
          keyValues,
          config,
        })
      );
      if (discoveryRevision !== discoveryRevisionV913.current) return null;
      const { success, message, data } = res.data;
      if (!success) {
        showError(message);
        return null;
      }
      setDiscoveryResult(data);
      if (data.source && !isInformationalModelDiscoverySourceV93(data.source)) {
        showWarning(getDiscoveryWarningMessage(data));
      }
      setOriginModelOptions((options) => {
        const optionMap = new Map(
          options.map((option) => [option.value, option])
        );
        data.models.forEach((modelName) => {
          optionMap.set(modelName, {
            key: modelName,
            text: modelName,
            value: modelName,
          });
        });
        return Array.from(optionMap.values());
      });
      return data;
    } catch (error) {
      if (discoveryRevision === discoveryRevisionV913.current) {
        showError(t(error.message));
      }
      return null;
    } finally {
      if (discoveryRevision === discoveryRevisionV913.current) {
        setDiscoveryLoading(false);
      }
    }
  };

  const importAllModels = async () => {
    const data = await fetchDiscoveredModels();
    if (!hasImportableDiscoveredModelsV816(data)) {
      return;
    }
    const rules = buildModelRulesSubmissionV822(modelMappingRowsV820, inputs.models, inputs.model_id_prefix);
    if (rules.error) {
      showInfo(modelRuleErrorTextV1015(rules, t));
      return;
    }
    try {
      handleInputChange(null, {
        name: 'models',
        value: importChannelModelsV1018(
          inputs.models, data.models, data.source, config,
          inputs.model_id_prefix, JSON.parse(rules.modelMapping || '{}')
        ),
      });
    } catch (error) {
      showError(`${t(error.message)}: ${error.model || ''}`);
    }
  };

  const clearDelistedModels = async () => {
    const data = await fetchDiscoveredModels();
    if (!canClearDelistedModelsV93(data)) {
      if (data) {
        showWarning(
          t('channel.edit.discovery.authoritative_list_required_v93')
        );
      }
      return;
    }
    const rules = buildModelRulesSubmissionV822(modelMappingRowsV820, inputs.models, inputs.model_id_prefix);
    if (rules.error) {
      showInfo(modelRuleErrorTextV1015(rules, t));
      return;
    }
    const mapping = JSON.parse(rules.modelMapping || '{}');
    const discoveredModels = new Set(data.models);
    const nextModels = inputs.models.filter((modelName) =>
      discoveredModels.has(upstreamModelV1018(modelName, mapping, inputs.model_id_prefix))
    );
    handleInputChange(null, {
      name: 'models',
      value: nextModels,
    });
    showSuccess(t('channel.edit.messages.clear_delisted_success'));
  };

  const openModelPicker = async () => {
    const data = await fetchDiscoveredModels();
    if (!hasImportableDiscoveredModelsV816(data)) {
      return;
    }
    const rules = buildModelRulesSubmissionV822(modelMappingRowsV820, inputs.models, inputs.model_id_prefix);
    if (rules.error) {
      showInfo(modelRuleErrorTextV1015(rules, t));
      return;
    }
    const mapping = JSON.parse(rules.modelMapping || '{}');
    const represented = new Set(inputs.models.map((name) => upstreamModelV1018(name, mapping, inputs.model_id_prefix)));
    setSelectedDiscoveredModels(
      data.models.filter((name) => represented.has(name))
    );
    setDiscoverySearch('');
    setModelPickerOpen(true);
  };

  const toggleDiscoveredModel = (modelName) => {
    setSelectedDiscoveredModels((models) => {
      if (models.includes(modelName)) {
        return models.filter((item) => item !== modelName);
      }
      return [...models, modelName];
    });
  };

  const confirmSelectedModels = () => {
    const rules = buildModelRulesSubmissionV822(modelMappingRowsV820, inputs.models, inputs.model_id_prefix);
    if (rules.error) {
      showInfo(modelRuleErrorTextV1015(rules, t));
      return;
    }
    try {
      const selected = (discoveryResult?.models || []).filter((id) =>
        selectedDiscoveredModels.includes(id));
      handleInputChange(null, {
        name: 'models',
        value: importChannelModelsV1018(
          inputs.models, selected, discoveryResult?.source, config,
          inputs.model_id_prefix, JSON.parse(rules.modelMapping || '{}'), true
        ),
      });
      setModelPickerOpen(false);
    } catch (error) {
      showError(`${t(error.message)}: ${error.model || ''}`);
    }
  };

  const addCustomModel = () => {
    if (customModel.trim() === '') return;
    const modelName = applyModelIdPrefix(customModel, inputs.model_id_prefix);
    if (inputs.models.includes(modelName)) return;
    let localModels = [...inputs.models];
    localModels.push(modelName);
    setCustomModel('');
    handleInputChange(null, { name: 'models', value: localModels });
  };

  const discoveredModelDisplayList = Array.from(new Set(discoveryResult?.models || []))
    .map((raw) => ({
      raw,
      name: applyModelIdPrefix(raw, inputs.model_id_prefix),
    }))
    .filter(({ raw, name }) =>
      [raw, name].some((value) => value.toLowerCase().includes(discoverySearch.toLowerCase()))
    );

  return (
    <div className='dashboard-container management-container-v4'>
      <Card fluid className='chart-card'>
        <Card.Content>
          <Card.Header className='header'>
            {isEdit
              ? t('channel.edit.title_edit')
              : t('channel.edit.title_create')}
          </Card.Header>
          <Form loading={loading} autoComplete='new-password'>
            <Form.Field>
              <Form.Select
                label={t('channel.edit.type')}
                name='type'
                required
                search
                options={CHANNEL_OPTIONS}
                value={inputs.type}
                onChange={handleInputChange}
              />
            </Form.Field>
            <Form.Field>
              <Form.Input
                label={t('channel.edit.name')}
                name='name'
                placeholder={t('channel.edit.name_placeholder')}
                onChange={handleInputChange}
                value={inputs.name}
                required
              />
            </Form.Field>
            <Form.Field>
              <Form.Dropdown
                label={t('channel.edit.group')}
                placeholder={t('channel.edit.group_placeholder')}
                name='groups'
                required
                fluid
                multiple
                selection
                allowAdditions
                additionLabel={t('channel.edit.group_addition')}
                onChange={handleInputChange}
                value={inputs.groups}
                autoComplete='new-password'
                options={groupOptions}
              />
            </Form.Field>
            {rootUser && !mineruChannelV913 && (
              <Form.Field>
                <Form.Dropdown
                  label={t('channel.edit.billing_cost_profile')}
                  placeholder={t(
                    'channel.edit.billing_cost_profile_placeholder'
                  )}
                  name='billing_provider_cost_profile'
                  fluid
                  search
                  selection
                  clearable
                  loading={costProfilesLoading}
                  options={costProfileOptions}
                  value={inputs.billing_provider_cost_profile || ''}
                  onChange={handleInputChange}
                />
                <small className='billing-muted-v8'>
                  {t('channel.edit.billing_cost_profile_hint')}
                </small>
              </Form.Field>
            )}
            {renderChannelTip(inputs.type)}

            {/* Azure OpenAI specific fields */}
            {inputs.type === 3 && (
              <>
                <Message>
                  注意，<strong>模型部署名称必须和模型名称保持一致</strong>
                  ，因为本系统会把请求体中的 model
                  参数替换为你的部署名称（模型名称中的点会被剔除），
                  <a
                    target='_blank'
                    href='https://github.com/songquanpeng/one-api/issues/133?notification_referrer_id=NT_kwDOAmJSYrM2NjIwMzI3NDgyOjM5OTk4MDUw#issuecomment-1571602271'
                  >
                    图片演示
                  </a>
                  。
                </Message>
                <Form.Field>
                  <Form.Input
                    label='AZURE_OPENAI_ENDPOINT'
                    name='base_url'
                    placeholder='请输入 AZURE_OPENAI_ENDPOINT，例如：https://docs-test-001.openai.azure.com'
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                  />
                </Form.Field>
                <Form.Field>
                  <Form.Input
                    label='默认 API 版本'
                    name='other'
                    placeholder='请输入默认 API 版本，例如：2024-03-01-preview，该配置可以被实际的请求查询参数所覆盖'
                    onChange={handleInputChange}
                    value={inputs.other}
                    autoComplete='new-password'
                  />
                </Form.Field>
              </>
            )}

            {/* Custom base URL field */}
            {inputs.type === 8 && (
              <Form.Field>
                <Form.Input
                  required
                  label={t('channel.edit.proxy_url')}
                  name='base_url'
                  placeholder={t('channel.edit.proxy_url_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 50 && (
              <>
                <Form.Field required>
                  <label>{t('channel.edit.base_url')}</label>
                  <Input
                    placeholder='https://cockpit-tools:1455'
                    onChange={handleOpenAIBaseURLHostChange}
                    value={baseURLHost}
                    autoComplete='new-password'
                    label={{ basic: true, content: 'Host' }}
                    labelPosition='left'
                  />
                </Form.Field>
                <Form.Field>
                  <label>API path suffix</label>
                  <Form.Dropdown
                    selection
                    options={BASE_URL_SUFFIX_OPTIONS}
                    onChange={handleOpenAIBaseURLSuffixChange}
                    value={baseURLSuffix}
                  />
                </Form.Field>
                {baseURLSuffix === '__custom__' && (
                  <Form.Field>
                    <Form.Input
                      label='Custom API path'
                      placeholder='/api/v1'
                      onChange={handleCustomBaseURLSuffixChange}
                      value={customBaseURLSuffix}
                      autoComplete='new-password'
                    />
                  </Form.Field>
                )}
                <Message size='small'>
                  Saved Base URL: <code>{inputs.base_url || '—'}</code>
                </Message>
              </>
            )}
            {Number(inputs.type) === 50 && (
              <>
                <Form.Dropdown
                  label={t('channel.edit.openlux_v1014.profile')}
                  aria-label={t('channel.edit.openlux_v1014.profile')}
                  name='openai_compatible_profile'
                  selection
                  options={buildOpenAICompatibleProfileOptionsV820(t)}
                  onChange={handleOpenAICompatibleProfileChangeV820}
                  value={config.openai_compatible_profile || ''}
                />
                {config.openai_compatible_profile === 'openlux' && (
                  <OpenLuxProfileV1014
                    config={config}
                    onChange={handleConfigChange}
                    t={t}
                    draft={openLuxDraftV1015}
                    onDraftChange={setOpenLuxDraftV1015}
                    disabled={savingV103 || loading}
                  />
                )}
              </>
            )}
            {inputs.type === 52 && (
              <Form.Field>
                <Form.Input
                  required
                  label='Video Task Base URL'
                  name='base_url'
                  placeholder='Enter the upstream base URL for the task-based video provider'
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}

            {mineruChannelV913 && (
              <>
                <Form.Input
                  label={t('channel.edit.mineru_v913.base_url')}
                  name='base_url'
                  placeholder={MINERU_BASE_URL_V913}
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
                <Form.Select
                  label={t('channel.edit.mineru_v913.mode')}
                  name='mineru_mode'
                  options={['precise', 'agent'].map((mode) => ({
                    key: mode,
                    value: mode,
                    text: t(`channel.edit.mineru_v913.${mode}`),
                  }))}
                  value={mineruModeV913}
                  onChange={handleMinerUModeChangeV913}
                  error={
                    !['precise', 'agent'].includes(mineruModeV913) &&
                    t('channel.edit.mineru_v913.invalid_mode')
                  }
                />
                <Label basic>
                  {t('channel.edit.mineru_v913.dedicated_protocol')}
                </Label>
              </>
            )}
            {inputs.type === 18 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.spark_version')}
                  name='other'
                  placeholder={t('channel.edit.spark_version_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 21 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.knowledge_id')}
                  name='other'
                  placeholder={t('channel.edit.knowledge_id_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 17 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.plugin_param')}
                  name='other'
                  placeholder={t('channel.edit.plugin_param_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.other}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Message>{t('channel.edit.coze_notice')}</Message>
            )}
            {inputs.type === 40 && (
              <Form.Field>
                <a
                  target='_blank'
                  rel='noopener noreferrer'
                  href='https://console.volcengine.com/ark/region:cn-beijing/docs/82379/1541523?lang=zh'
                >
                  {t('channel.edit.douban_notice_link')}
                  <Icon name='external alternate' />
                </a>
              </Form.Field>
            )}

            <Form.Field>
              <Checkbox
                toggle
                label={t('channel.edit.proxy_enabled')}
                checked={proxyEnabledV816}
                onChange={handleProxyEnabledChangeV816}
              />
            </Form.Field>
            {proxyEnabledV816 && (
              <Form.Field>
                <label>{t('channel.edit.proxy_type')}</label>
                <Button.Group fluid widths={2} size='small'>
                  <Button
                    type='button'
                    active={config.egress_mode === 'proxy'}
                    onClick={() => handleEgressModeChangeV816('proxy')}
                  >
                    <Icon name='globe' />
                    {t('channel.edit.egress_proxy')}
                  </Button>
                  <Button
                    type='button'
                    active={config.egress_mode === 'tailscale'}
                    onClick={() => handleEgressModeChangeV816('tailscale')}
                  >
                    <Icon name='server' />
                    {t('channel.edit.egress_tailscale')}
                  </Button>
                </Button.Group>
              </Form.Field>
            )}
            {config.egress_mode === 'proxy' && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.upstream_proxy')}
                  name='upstream_proxy'
                  placeholder={t('channel.edit.upstream_proxy_placeholder')}
                  onChange={handleConfigChange}
                  value={config.upstream_proxy || ''}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {config.egress_mode === 'tailscale' && (
              <Form.Field>
                <label>
                  {t('channel.edit.tailscale_server')}
                  <Button
                    type='button'
                    basic
                    compact
                    floated='right'
                    icon='refresh'
                    loading={tailscaleServersLoadingV816}
                    disabled={tailscaleServersLoadingV816}
                    title={t('channel.edit.tailscale_servers_refresh')}
                    aria-label={t('channel.edit.tailscale_servers_refresh')}
                    onClick={() => fetchTailscaleServersV816()}
                  />
                </label>
                <Form.Dropdown
                  fluid
                  selection
                  search
                  name='tailscale_server'
                  placeholder={t('channel.edit.tailscale_server_placeholder')}
                  options={tailscaleServerOptionsV816.map((option) => ({
                    ...option,
                    disabled: option.disabled || tailscaleServersFailedV91,
                  }))}
                  value={config.tailscale_server || ''}
                  onChange={handleTailscaleServerChangeV816}
                  onOpen={fetchTailscaleServersV816}
                  loading={tailscaleServersLoadingV816}
                  noResultsMessage={t(
                    'channel.edit.tailscale_server_no_results'
                  )}
                />
                {tailscaleServersErrorV816 && (
                  <Message
                    size='small'
                    negative
                    content={tailscaleServersErrorV816}
                  />
                )}
              </Form.Field>
            )}
            <FixedForwardedIPV1013
              config={config}
              type={inputs.type}
              onChange={handleConfigChange}
              t={t}
            />
            {!mineruChannelV913 && (
              <Form.Field>
                <Checkbox
                  label='Enable Cloudflare Worker Edge Proxy'
                  name='edge_proxy'
                  checked={config.edge_proxy === true}
                  disabled={
                    config.egress_mode === 'tailscale' ||
                    config.fixed_forwarded_ip_enabled_v1013 === true
                  }
                  onChange={handleConfigChange}
                />
              </Form.Field>
            )}

            <OpenRouterRoutingV1015
              type={inputs.type}
              config={config}
              onChange={handleConfigChange}
              t={t}
            />
            <VideoProtocolControlV1016
              channelType={inputs.type}
              value={config.video_protocol_v1016}
              operation={config.video_operation_v1016}
              onChange={(patch) => setConfig((current) => ({ ...current, ...patch }))}
            />
            <OutboundClientProfileV95
              type={inputs.type}
              groups={inputs.groups}
              config={config}
              onChange={handleConfigChange}
              t={t}
            />
            {!mineruChannelV913 && (
              <ImageResponsePolicyV103
                type={inputs.type}
                groups={inputs.groups}
                config={config}
                onChange={handleConfigChange}
                t={t}
              />
            )}
            {!mineruChannelV913 && (
              <ImageUpscalingV96
                type={inputs.type}
                models={inputs.models}
                config={config}
                onChange={handleConfigChange}
                t={t}
              />
            )}
            {!mineruChannelV913 && (
              <>
                <Form.Field>
                  <label>{t('channel.edit.test_endpoint.label')}</label>
                  <Form.Dropdown
                    selection
                    name='test_endpoint'
                    options={testEndpointOptions}
                    onChange={handleConfigChange}
                    value={config.test_endpoint || 'chat_completions'}
                  />
                  <Message
                    size='small'
                    info
                    content={t('channel.edit.test_endpoint.hint')}
                  />
                  {config.test_endpoint === 'images_generations' && (
                    <Message
                      size='small'
                      warning
                      content={t('channel.edit.test_endpoint.image_warning')}
                    />
                  )}
                </Form.Field>

                <Form.Field>
                  <label>
                    {t('channel.edit.upstream_input_protocol', {
                      defaultValue: '上游输入协议',
                    })}
                    <KeyHelpV107
                      label={t('channel.edit.upstream_input_help_label', {
                        defaultValue: '上游输入协议说明',
                      })}
                      content={t('channel.edit.upstream_input_help', {
                        defaultValue:
                          '决定发送给 Provider 的上游输入协议。自动直通不猜测端点；Chat-only 上游应选择 Chat，由已实现的适配器转换兼容请求。Key 响应格式不改变上游能力。',
                      })}
                    />
                  </label>
                  <Form.Dropdown
                    selection
                    options={
                      config.openai_compatible_profile === 'openlux'
                        ? [
                            {
                              key: 'openlux',
                              value: 'openlux',
                              text: t('channel.edit.openlux_v1014.rules'),
                            },
                          ]
                        : PROVIDER_INPUT_PROTOCOL_OPTIONS
                    }
                    disabled={config.openai_compatible_profile === 'openlux'}
                    onChange={handleProviderProtocolChange}
                    value={
                      config.openai_compatible_profile === 'openlux'
                        ? 'openlux'
                        : providerProfile.protocol || ''
                    }
                  />
                </Form.Field>
                {!providerProfile.protocol &&
                  config.openai_compatible_profile !== 'openlux' && (
                    <Message size='small' warning>
                      自动直通只会保留客户端请求协议，不会猜测 Provider
                      支持的端点。 如果上游只支持 Chat
                      Completions，请手动选择对应协议；这样 `/v1/responses`
                      才会被转换并发送到 `/v1/chat/completions`。
                    </Message>
                  )}
                {providerProfile.protocol && (
                  <>
                    <Form.Field>
                      <label>Provider 已确认支持的能力</label>
                      {PROVIDER_FEATURE_OPTIONS.filter(
                        (option) =>
                          providerProfile.protocol !== 'openai_chat' ||
                          option.chat
                      ).map((option) => (
                        <Checkbox
                          key={option.key}
                          label={option.label}
                          checked={(
                            providerProfile.supported_features || []
                          ).includes(option.key)}
                          onChange={(event, data) =>
                            handleProviderFeatureChange(
                              option.key,
                              data.checked
                            )
                          }
                          style={{
                            marginRight: '1.5rem',
                            marginBottom: '0.75rem',
                          }}
                        />
                      ))}
                    </Form.Field>
                    {providerProfile.protocol === 'openai_chat' && (
                      <Message size='small' warning>
                        V7 当前会转换普通文本、图片 URL、Function
                        tools、流式输出和 reasoning
                        effort。previous_response_id、store、compact、Hosted
                        Tools、MCP、Computer Use 与后台任务会在联系上游前返回
                        422， 不会再误发到 /v1/responses 后得到 404。
                      </Message>
                    )}
                  </>
                )}
              </>
            )}
            {inputs.type !== 43 && (
              <Form.Field>
                <Form.Input
                  label={t('channel.edit.model_id_prefix')}
                  name='model_id_prefix'
                  placeholder={t('channel.edit.model_id_prefix_placeholder')}
                  onChange={handleInputChange}
                  value={inputs.model_id_prefix || ''}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type !== 43 && (
              <Form.Field>
                <Form.Select
                  className='channel-model-sort-v107'
                  label={t('channel.edit.model_sort')}
                  name='model_sort_v107'
                  value={modelSortModeV107}
                  options={[
                    {
                      key: MODEL_SORT_MODE_ADDED_V107,
                      value: MODEL_SORT_MODE_ADDED_V107,
                      text: t('channel.edit.model_sort_added'),
                    },
                    {
                      key: MODEL_SORT_MODE_ALPHABETIC_V107,
                      value: MODEL_SORT_MODE_ALPHABETIC_V107,
                      text: t('channel.edit.model_sort_alphabetic'),
                    },
                  ]}
                  onChange={(_, { value }) => setModelSortModeV107(value)}
                />
                <div className='channel-model-select-v107'>
                  <Form.Dropdown
                    label={t('channel.edit.models')}
                    placeholder={t('channel.edit.models_placeholder')}
                    name='models'
                    required
                    fluid
                    multiple
                    search
                    onLabelClick={(e, { value }) => {
                      copy(value).then();
                    }}
                    selection
                    onChange={handleModelSelectionChangeV107}
                    value={displayedModelValuesV107}
                    autoComplete='new-password'
                    options={displayedModelOptionsV107}
                  />
                  <ChannelModelCopyV107
                    modelIds={inputs.models || []}
                    copyText={copy}
                    disabled={loading || savingV103}
                    t={t}
                  />
                </div>
              </Form.Field>
            )}
            {inputs.type !== 43 && (
              <>
                <ModelSyncV103
                  channelType={inputs.type}
                  config={config}
                  state={modelSyncStateV103}
                  onChange={handleConfigChange}
                  onSync={syncModelsV103}
                  saved={
                    isEdit && Boolean(savedChannelIdV103(channelId, inputs.id))
                  }
                  disabled={loading || savingV103 || discoveryLoading}
                  syncDisabled={
                    !modelSyncNeedsRefreshV103 &&
                    (!savedModelSyncEnabledV103 || inputs.status !== 1)
                  }
                  loading={modelSyncLoadingV103}
                  needsRefresh={modelSyncNeedsRefreshV103}
                  error={modelSyncErrorV103}
                  t={t}
                />
                {!isVideoTaskChannelV1() && discoveryResult && (
                  <Message
                    visible
                    info={isInformationalModelDiscoverySourceV93(
                      discoveryResult.source
                    )}
                    warning={
                      !isInformationalModelDiscoverySourceV93(
                        discoveryResult.source
                      )
                    }
                  >
                    <strong>
                      {getDiscoverySourceText(discoveryResult.source)}
                    </strong>
                    {' · '}
                    {getDiscoveryMessageText(discoveryResult)}
                    {discoveryResult.debug_endpoint && (
                      <div style={{ marginTop: '8px', wordBreak: 'break-all' }}>
                        <strong>{t('channel.edit.discovery.endpoint')}:</strong>{' '}
                        {discoveryResult.debug_endpoint}
                      </div>
                    )}
                    {discoveryResult.debug_error && (
                      <div
                        style={{ marginTop: '8px', wordBreak: 'break-word' }}
                      >
                        <strong>
                          {t('channel.edit.discovery.debug_error')}:
                        </strong>{' '}
                        {discoveryResult.debug_error}
                      </div>
                    )}
                  </Message>
                )}
                <div style={{ lineHeight: '40px', marginBottom: '12px' }}>
                  {!isVideoTaskChannelV1() && (
                    <>
                      <Button
                        type={'button'}
                        loading={discoveryLoading}
                        disabled={discoveryLoading || !canDiscoverModels()}
                        onClick={openModelPicker}
                      >
                        {t('channel.edit.buttons.fill_models')}
                      </Button>
                      <Button
                        type={'button'}
                        loading={discoveryLoading}
                        disabled={discoveryLoading || !canDiscoverModels()}
                        onClick={importAllModels}
                      >
                        {t('channel.edit.buttons.fill_all')}
                      </Button>
                      <Button
                        type={'button'}
                        loading={discoveryLoading}
                        disabled={
                          discoveryLoading ||
                          !canDiscoverModels() ||
                          mineruChannelV913
                        }
                        onClick={clearDelistedModels}
                      >
                        {t('channel.edit.buttons.clear_delisted')}
                      </Button>
                    </>
                  )}
                  <Button
                    type={'button'}
                    onClick={() => {
                      handleInputChange(null, { name: 'models', value: [] });
                    }}
                  >
                    {t('channel.edit.buttons.clear')}
                  </Button>
                  <Input
                    action={
                      <Button type={'button'} onClick={addCustomModel}>
                        {t('channel.edit.buttons.add_custom')}
                      </Button>
                    }
                    placeholder={t('channel.edit.buttons.custom_placeholder')}
                    value={customModel}
                    onChange={(e, { value }) => {
                      setCustomModel(value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        addCustomModel();
                        e.preventDefault();
                      }
                    }}
                  />
                </div>
                {!isVideoTaskChannelV1() && !canDiscoverModels() && (
                  <Message info>
                    {t('channel.edit.messages.base_url_required')}
                  </Message>
                )}
              </>
            )}
            {inputs.type !== 43 && (
              <>
                <div ref={modelRulesRefV1015}>
                  <ChannelModelMappingEditorV820
                    rows={modelMappingRowsV820}
                    channelModels={inputs.models}
                    modelIdPrefix={inputs.model_id_prefix}
                    onChange={setModelMappingRowsV820}
                    validation={modelPreviewDraftV822}
                    t={t}
                  />
                </div>
                {!mineruChannelV913 && (
                  <Form.Field>
                    <Form.TextArea
                      label={t('channel.edit.system_prompt')}
                      placeholder={t('channel.edit.system_prompt_placeholder')}
                      name='system_prompt'
                      onChange={handleInputChange}
                      value={inputs.system_prompt}
                      style={{
                        minHeight: 150,
                        fontFamily: 'JetBrains Mono, Consolas',
                      }}
                      autoComplete='new-password'
                    />
                  </Form.Field>
                )}
              </>
            )}
            {channelSecretsMaskedV815 && (
              <ChannelSecretReviewNoticeV815
                onReview={revealChannelSecretsV815}
              />
            )}
            {inputs.type === 33 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.aws_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                />
                <Form.Input
                  label='AK'
                  name='ak'
                  required
                  placeholder={t('channel.edit.aws_ak_placeholder')}
                  onChange={handleConfigChange}
                  value={config.ak}
                  disabled={channelSecretsMaskedV815}
                  autoComplete=''
                />
                <Form.Input
                  label='SK'
                  name='sk'
                  required
                  placeholder={t('channel.edit.aws_sk_placeholder')}
                  onChange={handleConfigChange}
                  value={config.sk}
                  disabled={channelSecretsMaskedV815}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type === 42 && (
              <Form.Field>
                <Form.Input
                  label='Region'
                  name='region'
                  required
                  placeholder={t('channel.edit.vertex_region_placeholder')}
                  onChange={handleConfigChange}
                  value={config.region}
                  autoComplete=''
                />
                <Header as='h4'>{t('channel.edit.vertex_credentials')}</Header>
                <Message
                  info
                  content={t('channel.edit.vertex_credentials_hint_v814')}
                />
                {keyRows.map((row, index) => {
                  const parsedKeyId = Number.parseInt(String(row.id), 10);
                  const canReenableKey =
                    row.status === 'disabled' && Number.isInteger(parsedKeyId);
                  const isRecoveringKey = recoveringKeyId === parsedKeyId;
                  let projectId = '';
                  try {
                    projectId = parseVertexCredentialV814(
                      row.key_value
                    ).projectId;
                  } catch (_) {}
                  return (
                    <div key={row.id} style={{ marginBottom: '16px' }}>
                      <Form.TextArea
                        label={`${t('channel.edit.vertex_credentials')} #${
                          index + 1
                        }`}
                        required
                        rows={8}
                        value={row.key_value}
                        disabled={row.key_masked}
                        placeholder={t(
                          'channel.edit.vertex_credentials_placeholder'
                        )}
                        onChange={(_, { value }) =>
                          updateKeyRow(index, { key_value: value })
                        }
                      />
                      {config.key_usage_strategy_v95 === 'coding_plan' && (
                        <CodingPlanKeyV95
                          row={row}
                          onChange={(patch) => updateKeyRow(index, patch)}
                          t={t}
                        />
                      )}
                      <Form.Group widths='equal'>
                        <Form.Input
                          type='number'
                          min={0}
                          max={VERTEX_MAX_CONCURRENCY_V815}
                          step={1}
                          label={t('channel.edit.vertex_llm_concurrency_v815')}
                          value={row.llm_concurrency_limit}
                          onChange={(_, { value }) =>
                            updateKeyRow(index, {
                              llm_concurrency_limit: value,
                            })
                          }
                        />
                        <Form.Input
                          type='number'
                          min={0}
                          max={VERTEX_MAX_CONCURRENCY_V815}
                          step={1}
                          label={t(
                            'channel.edit.vertex_image_concurrency_v815'
                          )}
                          value={row.image_concurrency_limit}
                          onChange={(_, { value }) =>
                            updateKeyRow(index, {
                              image_concurrency_limit: value,
                            })
                          }
                        />
                      </Form.Group>
                      <Message
                        size='tiny'
                        content={t('channel.edit.vertex_concurrency_hint_v815')}
                      />
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}
                      >
                        {projectId ? (
                          <Label basic color='blue'>
                            {t('channel.edit.vertex_project_detected_v814')}:{' '}
                            {projectId}
                          </Label>
                        ) : null}
                        <Label
                          as={canReenableKey ? 'a' : undefined}
                          color={getKeyStatusColor(row.status)}
                          onClick={
                            canReenableKey
                              ? () => handleEnableKeyV2(row)
                              : undefined
                          }
                          style={
                            canReenableKey
                              ? {
                                  cursor: 'pointer',
                                  opacity: isRecoveringKey ? 0.7 : 1,
                                }
                              : undefined
                          }
                        >
                          {isRecoveringKey
                            ? t('channel.edit.key_actions.reenable_loading')
                            : getKeyStatusText(row.status)}
                        </Label>
                        <Button.Group size='mini'>
                          {row.key_masked && (
                            <ChannelSecretReviewButtonV815
                              onReview={revealChannelSecretsV815}
                            />
                          )}
                          <Button
                            type='button'
                            icon='arrow up'
                            disabled={row.key_masked || index === 0}
                            title={t('channel.edit.key_actions.up')}
                            onClick={() => moveKeyRow(index, -1)}
                          />
                          <Button
                            type='button'
                            icon='arrow down'
                            disabled={
                              row.key_masked || index === keyRows.length - 1
                            }
                            title={t('channel.edit.key_actions.down')}
                            onClick={() => moveKeyRow(index, 1)}
                          />
                          <Button
                            type='button'
                            icon='trash alternate outline'
                            disabled={row.key_masked || keyRows.length === 1}
                            title={t('channel.edit.key_actions.remove')}
                            onClick={() => removeKeyRow(index)}
                          />
                        </Button.Group>
                      </div>
                      {row.last_error ? (
                        <Message warning size='small'>
                          <strong>
                            {t('channel.edit.key_meta.last_error')}:
                          </strong>{' '}
                          {row.last_error}
                        </Message>
                      ) : null}
                    </div>
                  );
                })}
                <Button
                  type='button'
                  disabled={channelSecretsMaskedV815}
                  onClick={addKeyRow}
                >
                  <Icon name='plus' />
                  {t('channel.edit.vertex_credentials_add_v814')}
                </Button>
                <Form.Dropdown
                  label={t('channel.edit.vertex_provider_service_tier_v817')}
                  name='vertex_ai_service_tier'
                  required
                  selection
                  options={buildVertexProviderServiceTierOptionsV817(t)}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_service_tier || 'standard'}
                />
                <Message
                  size='small'
                  info
                  content={t(
                    'channel.edit.vertex_provider_service_tier_hint_v817'
                  )}
                />
              </Form.Field>
            )}
            {inputs.type === 53 && (
              <Form.Field>
                <Message info>
                  {t('channel.edit.gemini_enterprise_hint_v815')}
                </Message>
                <Form.Input
                  label={t('channel.edit.gemini_enterprise_project_v815')}
                  name='gemini_enterprise_project_id'
                  required
                  placeholder={t(
                    'channel.edit.gemini_enterprise_project_placeholder_v815'
                  )}
                  onChange={handleConfigChange}
                  value={config.gemini_enterprise_project_id || ''}
                  autoComplete=''
                />
                <Form.Input
                  label={t('channel.edit.gemini_enterprise_location_v815')}
                  name='region'
                  required
                  placeholder='global'
                  onChange={handleConfigChange}
                  value={config.region || ''}
                  autoComplete=''
                />
                <Form.Dropdown
                  label={t('channel.edit.gemini_enterprise_service_tier_v816')}
                  name='vertex_ai_service_tier'
                  selection
                  options={buildGoogleGeminiServiceTierOptionsV816(t)}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_service_tier || 'standard'}
                />
                <Form.TextArea
                  label={t('channel.edit.gemini_enterprise_discovery_adc_v816')}
                  name='gemini_enterprise_discovery_adc'
                  rows={7}
                  placeholder={t(
                    'channel.edit.gemini_enterprise_discovery_adc_placeholder_v816'
                  )}
                  onChange={handleConfigChange}
                  value={config.gemini_enterprise_discovery_adc || ''}
                  disabled={
                    channelSecretsMaskedV815 ||
                    geminiEnterpriseDiscoveryADCMaskedV816
                  }
                  autoComplete='new-password'
                />
                <Message size='small' info>
                  {t('channel.edit.gemini_enterprise_discovery_adc_hint_v816')}
                </Message>
              </Form.Field>
            )}
            {usesCompatibleServiceTierV1015(
              inputs.type,
              inputs.groups,
              config
            ) && (
              <Form.Field>
                {isOpenAICompatibleVertexGroupV817(
                  inputs.type,
                  inputs.groups
                ) && (
                  <Message
                    info
                    content={t(
                      'channel.edit.vertex_openai_compatible_hint_v817'
                    )}
                  />
                )}
                <Message
                  size='small'
                  info
                  content={t(
                    'channel.edit.vertex_openai_compatible_profile_hint_v820'
                  )}
                />
                <Form.Dropdown
                  label={t('channel.edit.vertex_provider_service_tier_v817')}
                  name='vertex_ai_service_tier'
                  required
                  selection
                  options={buildVertexOpenAICompatibleServiceTierOptionsV817(
                    t,
                    config.openai_compatible_profile || ''
                  )}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_service_tier || 'standard'}
                />
                <Message
                  size='small'
                  info
                  content={t(
                    'channel.edit.vertex_openai_compatible_service_tier_hint_v817'
                  )}
                />
              </Form.Field>
            )}
            {inputs.type === 24 && (
              <Form.Field>
                <Message info>
                  {t('channel.edit.google_ai_studio_hint_v816')}
                </Message>
                <Form.Input
                  label={t('channel.edit.google_ai_studio_project_v816')}
                  name='gemini_api_project_id'
                  placeholder={t(
                    'channel.edit.google_ai_studio_project_placeholder_v816'
                  )}
                  onChange={handleConfigChange}
                  value={config.gemini_api_project_id || ''}
                  autoComplete=''
                />
                <Form.Dropdown
                  label={t('channel.edit.google_gemini_service_tier_v816')}
                  name='vertex_ai_service_tier'
                  selection
                  options={buildGoogleGeminiServiceTierOptionsV816(t)}
                  onChange={handleConfigChange}
                  value={config.vertex_ai_service_tier || 'standard'}
                />
              </Form.Field>
            )}
            {inputs.type === 34 && (
              <Form.Input
                label={t('channel.edit.user_id')}
                name='user_id'
                required
                placeholder={t('channel.edit.user_id_placeholder')}
                onChange={handleConfigChange}
                value={config.user_id}
                autoComplete=''
              />
            )}
            <ChannelKeyPolicyV95
              config={config}
              onChange={handleConfigChange}
              t={t}
            />
            <CodingPlanPolicyV95
              config={config}
              onChange={handleConfigChange}
              t={t}
            />
            {mineruAgentV913 && (
              <Label basic>{t('channel.edit.mineru_v913.no_auth')}</Label>
            )}
            {inputs.type !== 33 && inputs.type !== 42 && !mineruAgentV913 && (
              <>
                <Header as='h4'>
                  {t(
                    mineruChannelV913
                      ? 'channel.edit.mineru_v913.token'
                      : 'channel.edit.key'
                  )}
                </Header>
                {!mineruChannelV913 &&
                  config.key_usage_strategy_v95 !== 'coding_plan' && (
                    <Message info>{t('channel.edit.key_helper')}</Message>
                  )}
                {keyRows.map((row, index) => {
                  const parsedKeyId = Number.parseInt(String(row.id), 10);
                  const canReenableKey =
                    row.status === 'disabled' && Number.isInteger(parsedKeyId);
                  const isRecoveringKey = recoveringKeyId === parsedKeyId;

                  return (
                    <div key={row.id} style={{ marginBottom: '12px' }}>
                      <Input
                        fluid
                        value={row.key_value}
                        readOnly={row.key_masked}
                        placeholder={type2secretPrompt(inputs.type, t)}
                        onChange={(e, { value }) =>
                          updateKeyRow(index, { key_value: value })
                        }
                        action
                      >
                        <input />
                        {row.key_masked && (
                          <ChannelSecretReviewButtonV815
                            onReview={revealChannelSecretsV815}
                          />
                        )}
                        <Button
                          type='button'
                          icon
                          onClick={() => moveKeyRow(index, -1)}
                          disabled={row.key_masked || index === 0}
                          title={t('channel.edit.key_actions.up')}
                        >
                          <Icon name='arrow up' />
                        </Button>
                        <Button
                          type='button'
                          icon
                          onClick={() => moveKeyRow(index, 1)}
                          disabled={
                            row.key_masked || index === keyRows.length - 1
                          }
                          title={t('channel.edit.key_actions.down')}
                        >
                          <Icon name='arrow down' />
                        </Button>
                        <Button
                          type='button'
                          icon
                          onClick={() => removeKeyRow(index)}
                          disabled={row.key_masked}
                          title={t('channel.edit.key_actions.remove')}
                        >
                          <Icon name='trash alternate outline' />
                        </Button>
                      </Input>
                      {config.key_usage_strategy_v95 === 'coding_plan' && (
                        <CodingPlanKeyV95
                          row={row}
                          onChange={(patch) => updateKeyRow(index, patch)}
                          t={t}
                        />
                      )}
                      {inputs.type === 53 && (
                        <>
                          <Form.Group
                            widths='equal'
                            style={{ marginTop: '10px' }}
                          >
                            <Form.Input
                              type='number'
                              min={0}
                              max={VERTEX_MAX_CONCURRENCY_V815}
                              step={1}
                              label={t(
                                'channel.edit.vertex_llm_concurrency_v815'
                              )}
                              value={row.llm_concurrency_limit}
                              onChange={(_, { value }) =>
                                updateKeyRow(index, {
                                  llm_concurrency_limit: value,
                                })
                              }
                            />
                            <Form.Input
                              type='number'
                              min={0}
                              max={VERTEX_MAX_CONCURRENCY_V815}
                              step={1}
                              label={t(
                                'channel.edit.vertex_image_concurrency_v815'
                              )}
                              value={row.image_concurrency_limit}
                              onChange={(_, { value }) =>
                                updateKeyRow(index, {
                                  image_concurrency_limit: value,
                                })
                              }
                            />
                          </Form.Group>
                          <Message
                            size='tiny'
                            content={t(
                              'channel.edit.vertex_concurrency_hint_v815'
                            )}
                          />
                        </>
                      )}
                      <div
                        style={{
                          marginTop: '8px',
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <Label
                          as={canReenableKey ? 'a' : undefined}
                          color={getKeyStatusColor(row.status)}
                          onClick={
                            canReenableKey
                              ? () => handleEnableKeyV2(row)
                              : undefined
                          }
                          style={
                            canReenableKey
                              ? {
                                  cursor: 'pointer',
                                  opacity: isRecoveringKey ? 0.7 : 1,
                                }
                              : undefined
                          }
                          title={
                            canReenableKey
                              ? t('channel.edit.key_actions.reenable')
                              : undefined
                          }
                        >
                          {isRecoveringKey
                            ? t('channel.edit.key_actions.reenable_loading')
                            : getKeyStatusText(row.status)}
                        </Label>
                        {row.last_used_at && (
                          <Label basic>
                            {t('channel.edit.key_meta.last_used')}:{' '}
                            {formatDateTime(row.last_used_at)}
                          </Label>
                        )}
                        {row.cooldown_until && (
                          <Label basic color='orange'>
                            {t('channel.edit.key_meta.cooldown_until')}:{' '}
                            {formatDateTime(row.cooldown_until)}
                          </Label>
                        )}
                      </div>
                      {row.last_error && (
                        <Message warning size='small'>
                          <strong>
                            {t('channel.edit.key_meta.last_error')}:
                          </strong>{' '}
                          {row.last_error}
                        </Message>
                      )}
                    </div>
                  );
                })}
                <Button
                  type='button'
                  disabled={channelSecretsMaskedV815}
                  onClick={addKeyRow}
                >
                  <Icon name='plus' />
                  {t('channel.edit.key_actions.add')}
                </Button>
              </>
            )}
            {inputs.type === 37 && (
              <Form.Field>
                <Form.Input
                  label='Account ID'
                  name='user_id'
                  required
                  placeholder={
                    '请输入 Account ID，例如：d8d7c61dbc334c32d3ced580e4bf42b4'
                  }
                  onChange={handleConfigChange}
                  value={config.user_id}
                  autoComplete=''
                />
              </Form.Field>
            )}
            {inputs.type !== 3 &&
              inputs.type !== 33 &&
              inputs.type !== 8 &&
              inputs.type !== 50 &&
              inputs.type !== 52 &&
              !mineruChannelV913 &&
              inputs.type !== 22 && (
                <Form.Field>
                  <Form.Input
                    label={inputs.type === 56 ? 'Scrapling MCP URL' : inputs.type === 55 ? 'SearXNG Base URL' : t('channel.edit.proxy_url')}
                    name='base_url'
                    placeholder={t('channel.edit.proxy_url_placeholder')}
                    onChange={handleInputChange}
                    value={inputs.base_url}
                    autoComplete='new-password'
                  />
                </Form.Field>
              )}
            {inputs.type === 55 && (
              <SearchEnginesV116 config={config} onChange={handleConfigChange}
                disabled={loading || savingV103} t={t} />
            )}
            {inputs.type === 56 && (
              <Form.Checkbox
                label={t('channel.edit.scrape_v116.isolation')}
                checked={config.scrape_egress_isolated_v116 === true}
                onChange={(_, { checked }) => handleConfigChange(null, { name: 'scrape_egress_isolated_v116', value: checked })}
              />
            )}
            {inputs.type === 22 && (
              <Form.Field>
                <Form.Input
                  label='私有部署地址'
                  name='base_url'
                  placeholder={
                    '请输入私有部署地址，格式为：https://fastgpt.run/api/openapi'
                  }
                  onChange={handleInputChange}
                  value={inputs.base_url}
                  autoComplete='new-password'
                />
              </Form.Field>
            )}
            {inputs.type !== 43 && (
              <>
              <PublicModelIDsV113
                config={config}
                onChange={handleConfigChange}
                disabled={loading || savingV103 || modelSyncLoadingV103}
                t={t}
              />
              <ChannelModelPreviewV822
                draft={modelPreviewDraftV822}
                channel={{
                  name: inputs.name,
                  id: inputs.id || channelId,
                  group: inputs.groups,
                  base_url: inputs.base_url,
                }}
                scopeKey={channelId || 'new'}
                disabled={loading}
                requestPreview={requestModelPreviewV822}
                copyText={copy}
                t={t}
              />
              </>
            )}
            <Button onClick={handleCancel}>
              {t('channel.edit.buttons.cancel')}
            </Button>
            <Button
              type={isEdit ? 'button' : 'submit'}
              positive
              loading={savingV103}
              disabled={
                loading ||
                savingV103 ||
                modelSyncLoadingV103 ||
                modelSyncNeedsRefreshV103 ||
                (isEdit && !savedChannelIdV103(channelId, inputs.id))
              }
              onClick={submit}
            >
              {t('channel.edit.buttons.submit')}
            </Button>
          </Form>
        </Card.Content>
      </Card>
      <Modal
        open={modelPickerOpen}
        onClose={() => setModelPickerOpen(false)}
        size='small'
      >
        <Modal.Header>{t('channel.edit.buttons.fill_models')}</Modal.Header>
        <Modal.Content>
          <Header as='h5'>
            {discoveryResult
              ? getDiscoverySourceText(discoveryResult.source)
              : t('channel.edit.discovery.sources.dynamic')}
          </Header>
          {mineruChannelV913 && (
            <Label basic>{t('channel.edit.mineru_v913.capabilities')}</Label>
          )}
          {discoveryResult?.source === 'reference_catalog' && (
            <Message info>{getDiscoveryMessageText(discoveryResult)}</Message>
          )}
          <Input
            fluid
            icon='search'
            placeholder={t('channel.edit.discovery.search_placeholder')}
            value={discoverySearch}
            onChange={(e, { value }) => setDiscoverySearch(value)}
            style={{ marginBottom: '12px' }}
          />
          <div className='channel-model-import-v111__list'>
            {discoveredModelDisplayList.map(({ raw, name }) => (
                <div key={raw} className='channel-model-import-v111__row' title={raw}>
                  <Checkbox
                    label={name || raw}
                    checked={selectedDiscoveredModels.includes(raw)}
                    onChange={() => toggleDiscoveredModel(raw)}
                  />
                </div>
              ))}
            {discoveredModelDisplayList.length === 0 && (
              <Message>{t('channel.edit.discovery.empty')}</Message>
            )}
          </div>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setModelPickerOpen(false)}>
            {t('channel.edit.buttons.cancel')}
          </Button>
          <Button positive onClick={confirmSelectedModels}>
            {t('channel.edit.discovery.confirm')}
          </Button>
        </Modal.Actions>
      </Modal>
      <RootStepUpModalV815
        open={rootStepUpOpenV815}
        onClose={() => setRootStepUpOpenV815(false)}
        onVerified={() => {
          setRootStepUpOpenV815(false);
          revealChannelSecretsV815();
        }}
      />
    </div>
  );
};

export default EditChannel;
